import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  confirmationModalAtom,
  formModalAtom,
  inputModalAtom,
  successModalAtom
} from './atoms';
import { FormModalField } from './types';
import { ConfirmationModalType } from './constants';
import React from 'react';

const useConfirmationModal = () => {
  const setModal = useSetRecoilState(confirmationModalAtom);

  const showModal = ({
    message,
    title,
    onConfirm,
    confirmationText,
    cancelText,
    modalType,
    onClose,
    onDestructiveConfirm,
    destructiveText,
    width,
    showSecondaryButton,
    secondaryText,
    onSecondaryClick,
    hideCancel,
    showDontShowAgainCheckbox
  }: {
    message?: string | React.ReactNode;
    title: string;
    onConfirm?: (dontShowAgainCheck?: boolean) => void;
    confirmationText?: string;
    cancelText?: string;
    modalType?: ConfirmationModalType;
    onClose?: () => void;
    onDestructiveConfirm?: () => void;
    destructiveText?: string;
    width?: number;
    showSecondaryButton?: boolean;
    secondaryText?: string;
    onSecondaryClick?: () => void;
    hideCancel?: boolean;
    showDontShowAgainCheckbox?: boolean;
  }) => {
    setModal({
      isOpen: true,
      message: message,
      title,
      onConfirm: onConfirm,
      confirmationText,
      cancelText,
      modalType: modalType || ConfirmationModalType.DEFAULT,
      onClose: onClose,
      onDestructiveConfirm: onDestructiveConfirm,
      destructiveText,
      showSecondaryButton,
      secondaryText,
      onSecondaryClick,
      width,
      hideCancel,
      showDontShowAgainCheckbox
    });
  };

  const hideModal = () => {
    setModal({
      isOpen: false,
      message: '',
      onConfirm: () => undefined,
      title: '',
      confirmationText: undefined,
      modalType: ConfirmationModalType.DEFAULT,
      hideCancel: false
    });
  };

  return { showModal, hideModal };
};

const useFormModal = <FormValues>() => {
  const [, setState] = useRecoilState(formModalAtom);

  const openModal = ({
    fields,
    onSubmit,
    title,
    subtitle
  }: {
    fields: FormModalField[];
    onSubmit: (values: FormValues) => void;
    title: string;
    subtitle?: string;
  }) => {
    setState({
      isOpen: true,
      fields,
      onSubmit,
      title,
      subtitle
    });
  };

  const closeModal = () => {
    setState({
      fields: [],
      title: '',
      isOpen: false
    });
  };

  return { openModal, closeModal };
};

const useSuccessModal = () => {
  const [, setState] = useRecoilState(successModalAtom);

  const openModal = ({
    title,
    subtitle,
    onConfirm
  }: {
    title: string;
    subtitle?: string;
    onConfirm?: () => void;
  }) => {
    setState({
      isOpen: true,
      title,
      subtitle,
      onConfirm
    });
  };

  const closeModal = () => {
    setState({
      title: '',
      isOpen: false,
      subtitle: '',
      onConfirm: () => undefined
    });
  };

  return { openModal, closeModal };
};

const useInputModal = () => {
  const setModal = useSetRecoilState(inputModalAtom);

  const showModal = ({
    title,
    inputLabel,
    onConfirmClick,
    initialValue,
    inputSubLabel
  }: {
    title: string;
    inputLabel: string;
    onConfirmClick: (inputtedValue: string) => void;
    initialValue?: string;
    inputSubLabel?: string;
  }) => {
    setModal({
      isOpen: true,
      title,
      inputLabel,
      onConfirmClick,
      initialValue,
      inputSubLabel
    });
  };

  const hideModal = () => {
    setModal({
      isOpen: false,
      title: '',
      inputLabel: '',
      initialValue: undefined,
      onConfirmClick: () => undefined
    });
  };

  return { showModal, hideModal };
};

export { useConfirmationModal, useFormModal, useSuccessModal, useInputModal };
